import type { Project } from '../../../aansluiting/project/types/Project'
import type { HoofdleidingProject } from '../../../hoofdleiding/project/types/HoofdleidingProject'
import type { EnhancedLinkedProject } from '../types/EnhancedLinkedProject'
import type { LinkedProjectGroup } from '../types/LinkedProjectGroup'
import type { ProjectGroup } from '../types/ProjectGroup'

export const groupProjectAfhankelijkheden = ({
  projects,
  projectGroups,
}: {
  projects: Partial<Project | HoofdleidingProject>[]
  projectGroups: ProjectGroup[]
}): LinkedProjectGroup[] => {
  const enhancedProjects = projects.map(project2enhancedProject)

  const hlProjecten = enhancedProjects.filter((p) => p.type === 'Hoofdleiding')
  const alProjecten = enhancedProjects.filter((p) => p.type === 'Aanvraag')

  // Edge case: only 1 AL project and no HL projects
  if (hlProjecten.length === 0 && alProjecten.length === 1) {
    return [
      {
        groupedAlProjecten: [],
        alProjecten: [alProjecten[0]],
      },
    ]
  }

  return hlProjecten.length === 0
    ? generateAansluiting2Aansluiting({ projectGroups, alProjecten })
    : generateAansluiting2Hoofdleiding({
        projectGroups,
        hlProjecten,
        alProjecten,
      })
}

const project2enhancedProject = (
  project: Partial<Project | HoofdleidingProject>,
) => {
  const enhancedType = project.betrokkenen
    ? project.type
    : `Unauthorized${project.type}`

  return { ...project, enhancedType } as EnhancedLinkedProject
}

const generateAansluiting2Aansluiting = ({
  projectGroups,
  alProjecten,
}: {
  projectGroups: ProjectGroup[]
  alProjecten: EnhancedLinkedProject[]
}) => {
  const groupedAlProjecten = projectGroups.map((group) => {
    return group.projects
      .map((projectId) => {
        return alProjecten.find((p) => p.aanvraagID === projectId)
      })
      .filter(Boolean) as EnhancedLinkedProject[]
  })

  return [{ hlProject: undefined, groupedAlProjecten, alProjecten: [] }]
}

const generateAansluiting2Hoofdleiding = ({
  projectGroups,
  hlProjecten,
  alProjecten,
}: {
  projectGroups: ProjectGroup[]
  hlProjecten: EnhancedLinkedProject[]
  alProjecten: EnhancedLinkedProject[]
}) => {
  const alIdToGroupDictionary = projectGroups.reduce(
    (dict, group) => {
      group.projects.forEach((projectId) => {
        dict[projectId] = group._id
      })
      return dict
    },
    {} as Record<string, string>,
  )

  const buildLinkedProjectTableRow = (hlProject: EnhancedLinkedProject) => {
    const grouped: Record<string, EnhancedLinkedProject[]> = {}

    const tr: LinkedProjectGroup = {
      hlProject,
      groupedAlProjecten: [],
      alProjecten: [],
    }

    for (const aanvraagID of hlProject.linkedProjects || []) {
      const alProject = alProjecten.find((p) => p.aanvraagID === aanvraagID)
      const groupId = alIdToGroupDictionary[aanvraagID]

      if (!groupId && alProject) {
        tr.alProjecten.push(alProject)
        continue
      }

      if (!grouped[groupId]) {
        grouped[groupId] = []
      }

      if (alProject) {
        grouped[groupId].push(alProject)
      }
    }

    tr.groupedAlProjecten = Object.values(grouped)

    return tr
  }

  return hlProjecten.map(buildLinkedProjectTableRow)
}
